import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Markdown from "react-markdown";
import { PdfSelectionToolbar } from "./PdfSelectionToolbar";
import { rehypeHighlights, type HighlightEntry } from "@/lib/rehype-highlights";
import type { PaperHighlight } from "@/hooks/useHighlights";

interface SelectionState {
  text: string;
  x: number;
  y: number;
}

interface Props {
  text: string;
  onAppendToAbstract?: (text: string) => void;
  onAddAsChunk?: (text: string) => void;
  onExplain?: (text: string) => void;
  onStashEvidence?: (text: string) => void;
  highlightText?: string;
  onHighlight?: (text: string, charOffset?: number) => void;
  highlights?: PaperHighlight[];
  selectedIds?: string[];
  focusedHighlightId?: string | null;
  onToggleHighlight?: (highlightId: string) => void;
  maximized?: boolean;
}

export function FullTextViewer({
  text,
  onAppendToAbstract,
  onAddAsChunk,
  onExplain,
  onStashEvidence,
  highlightText,
  onHighlight,
  highlights,
  selectedIds,
  focusedHighlightId,
  onToggleHighlight,
  maximized,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<SelectionState | null>(null);

  const hasSelectionActions =
    onAppendToAbstract || onAddAsChunk || onExplain || onStashEvidence;

  // Highlight the excerpt text in the rendered markdown
  useEffect(() => {
    if (!highlightText || !containerRef.current) return;
    // Remove any previous marks
    containerRef.current.querySelectorAll("mark[data-evidence]").forEach((m) => {
      const parent = m.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(m.textContent || ""), m);
        parent.normalize();
      }
    });
    // Build full visible text from container to find the excerpt position
    const el = containerRef.current;
    const fullText = el.innerText || el.textContent || "";
    // Normalize whitespace in both for matching
    const normalize = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();
    const normalizedFull = normalize(fullText);
    const normalizedExcerpt = normalize(highlightText);
    // Use first 100 chars to find approximate position
    const searchPrefix = normalizedExcerpt.slice(0, 100);
    const matchIdx = normalizedFull.indexOf(searchPrefix);
    if (matchIdx < 0) return;

    // Walk text nodes to find and highlight the matching range
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let charCount = 0;
    let node: Text | null;
    let started = false;
    const excerptLen = Math.min(normalizedExcerpt.length, 500);
    const targetEnd = matchIdx + excerptLen;
    let firstMark: HTMLElement | null = null;

    while ((node = walker.nextNode() as Text | null)) {
      const nodeText = node.textContent || "";
      const nodeStart = charCount;
      // Count normalized chars (collapse whitespace to match normalizedFull)
      const nodeNormLen = normalize(nodeText).length;
      const nodeEnd = nodeStart + nodeNormLen;

      if (nodeEnd > matchIdx && nodeStart < targetEnd) {
        // This text node overlaps with the excerpt
        const startInNode = started ? 0 : Math.max(0, matchIdx - nodeStart);
        const endInNode = Math.min(nodeText.length, nodeText.length - Math.max(0, nodeEnd - targetEnd));
        try {
          const range = document.createRange();
          range.setStart(node, Math.min(startInNode, nodeText.length));
          range.setEnd(node, Math.min(endInNode, nodeText.length));
          const mark = document.createElement("mark");
          mark.setAttribute("data-evidence", "true");
          mark.className = "bg-yellow-200 dark:bg-yellow-800/50";
          range.surroundContents(mark);
          if (!firstMark) firstMark = mark;
          started = true;
        } catch {
          // surroundContents can fail on complex DOM — skip this node
        }
      }
      charCount = nodeEnd;
      if (charCount >= targetEnd) break;
    }
    firstMark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightText]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleMouseUp = () => {
      const sel = window.getSelection();
      const selectedText = sel?.toString().trim();
      if (!selectedText || selectedText.length < 3) return;

      const range = sel?.getRangeAt(0);
      if (!range) return;

      const clientRects = range.getClientRects();
      const rect = clientRects.length > 0 ? clientRects[clientRects.length - 1] : range.getBoundingClientRect();
      const containerRect = el.getBoundingClientRect();

      setSelection({
        text: selectedText,
        x: rect.left - containerRect.left + rect.width / 2 - 100,
        y: rect.top - containerRect.top + el.scrollTop - 40,
      });
    };

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-testid='pdf-selection-toolbar']")) {
        setSelection(null);
      }
    };

    el.addEventListener("mouseup", handleMouseUp);
    el.addEventListener("mousedown", handleMouseDown);

    return () => {
      el.removeEventListener("mouseup", handleMouseUp);
      el.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);

  // Build highlight entries for the rehype plugin
  const highlightEntries = useMemo((): HighlightEntry[] => {
    if (!highlights?.length) return [];
    return highlights.map((h) => ({
      id: h.id,
      text: h.text,
      state: focusedHighlightId === h.id
        ? "focused"
        : selectedIds?.includes(h.id)
          ? "selected"
          : "unselected",
      // Extract charOffset from rects if stored (HTML highlights use [{charOffset: N}])
      charOffset: (h.rects?.[0] as Record<string, unknown>)?.charOffset as number | undefined,
    }));
  }, [highlights, selectedIds, focusedHighlightId]);

  const rehypePlugins = useMemo(
    () => highlightEntries.length > 0
      ? [[rehypeHighlights, { highlights: highlightEntries }] as any]
      : [],
    [highlightEntries],
  );

  // Scroll to focused highlight
  useEffect(() => {
    if (!focusedHighlightId || !containerRef.current) return;
    const mark = containerRef.current.querySelector(
      `mark[data-highlight-id="${focusedHighlightId}"]`
    );
    mark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusedHighlightId]);

  // Click handler for highlight toggle
  const handleClick = useCallback((e: React.MouseEvent) => {
    const mark = (e.target as HTMLElement).closest("mark[data-highlight-id]");
    if (mark && onToggleHighlight) {
      const id = mark.getAttribute("data-highlight-id");
      if (id) onToggleHighlight(id);
    }
  }, [onToggleHighlight]);

  return (
    <div ref={containerRef} onClick={handleClick} className={`relative overflow-y-auto p-4 ${maximized ? "flex-1" : "max-h-[calc(100vh-10rem)]"}`}>
      <article className="prose prose-sm dark:prose-invert max-w-none">
        <Markdown rehypePlugins={rehypePlugins}>{text}</Markdown>
      </article>

      {selection && (hasSelectionActions || onHighlight) && (
        <PdfSelectionToolbar
          selectedText={selection.text}
          position={{ x: selection.x, y: selection.y }}
          onAppendToAbstract={onAppendToAbstract ?? (() => {})}
          onAddAsChunk={onAddAsChunk ?? (() => {})}
          onExplain={onExplain}
          onStashEvidence={onStashEvidence}
          onHighlight={onHighlight ? () => {
      // Compute character offset for unique anchoring of HTML highlights
      let charOffset: number | undefined;
      const sel = window.getSelection();
      if (sel?.rangeCount && containerRef.current) {
        const range = sel.getRangeAt(0);
        const preRange = document.createRange();
        preRange.setStart(containerRef.current, 0);
        preRange.setEnd(range.startContainer, range.startOffset);
        charOffset = preRange.toString().length;
      }
      onHighlight(selection.text, charOffset);
      setSelection(null);
    } : undefined}
          onDismiss={() => setSelection(null)}
        />
      )}
    </div>
  );
}
